import { useState, type ReactNode } from 'react';
import { useQueryClient, type QueryClient, type QueryKey } from '@tanstack/react-query';
import type { LeadWithRelations } from '@/types';
import { useCancelLead, useUpdateLeadDetails } from '@/hooks/useLeadActions';
import { canMoveLead, stageOf, type LeadStage } from '@/components/leads/leadStages';
import { FollowUpDialog } from '@/components/leads/FollowUpDialog';
import { CancelLeadDialog } from '@/components/leads/CancelLeadDialog';
import { ConvertToJobWizard } from '@/components/leads/ConvertToJobWizard';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

type LeadsSnapshot = Array<[QueryKey, unknown]>;

/** Patch one lead in every cached leads list; returns what to restore on failure. */
async function patchLeadInCache(
  queryClient: QueryClient,
  leadId: string,
  patch: Partial<LeadWithRelations>,
): Promise<LeadsSnapshot> {
  await queryClient.cancelQueries({ queryKey: ['leads'] });
  const snapshot = queryClient.getQueriesData<unknown>({ queryKey: ['leads'] });
  queryClient.setQueriesData<unknown>({ queryKey: ['leads'] }, (old) =>
    Array.isArray(old)
      ? old.map((lead: LeadWithRelations) => (lead?.id === leadId ? { ...lead, ...patch } : lead))
      : old,
  );
  return snapshot;
}

/**
 * Moves a lead between board stages. Stages that need input open the same
 * dialog the lead detail uses; the lead only changes once that dialog saves.
 * Render `dialogs` once, anywhere under the caller.
 */
export function useLeadStageMove(): {
  requestMove: (lead: LeadWithRelations, to: LeadStage) => void;
  dialogs: ReactNode;
} {
  const queryClient = useQueryClient();
  const updateLead = useUpdateLeadDetails();
  const cancelLead = useCancelLead();

  const [followUpLead, setFollowUpLead] = useState<LeadWithRelations | null>(null);
  const [lostLead, setLostLead] = useState<LeadWithRelations | null>(null);
  const [reopenLead, setReopenLead] = useState<LeadWithRelations | null>(null);
  // The wizard shows its success dialog after it closes, so it stays mounted
  // with the last lead until the next conversion is requested.
  const [convertLead, setConvertLead] = useState<LeadWithRelations | null>(null);
  const [convertOpen, setConvertOpen] = useState(false);

  const moveToNew = async (lead: LeadWithRelations) => {
    const updates: Record<string, unknown> = { status: 'new' };
    if (stageOf(lead.status) === 'lost') updates.lost_reason = null;

    const snapshot = await patchLeadInCache(queryClient, lead.id, updates as Partial<LeadWithRelations>);
    updateLead.mutate(
      { leadId: lead.id, updates },
      { onError: () => snapshot.forEach(([key, data]) => queryClient.setQueryData(key, data)) },
    );
  };

  const requestMove = (lead: LeadWithRelations, to: LeadStage) => {
    const from = stageOf(lead.status);
    if (from === to || !canMoveLead(from, to)) return;

    switch (to) {
      case 'new':
        if (from === 'lost') setReopenLead(lead);
        else void moveToNew(lead);
        break;
      case 'needs_follow_up':
        setFollowUpLead(lead);
        break;
      case 'converted':
        setConvertLead(lead);
        setConvertOpen(true);
        break;
      case 'lost':
        setLostLead(lead);
        break;
    }
  };

  const dialogs = (
    <>
      {followUpLead && (
        <FollowUpDialog
          leadId={followUpLead.id}
          firstFollowUpAt={followUpLead.first_follow_up_at}
          clearLostReason={stageOf(followUpLead.status) === 'lost'}
          open
          onOpenChange={(open) => !open && setFollowUpLead(null)}
        />
      )}

      <CancelLeadDialog
        open={!!lostLead}
        onOpenChange={(open) => !open && setLostLead(null)}
        isLoading={cancelLead.isPending}
        onConfirm={(reason) => {
          if (!lostLead) return;
          cancelLead.mutate({ leadId: lostLead.id, reason }, { onSuccess: () => setLostLead(null) });
        }}
      />

      {convertLead && (
        <ConvertToJobWizard
          key={convertLead.id}
          lead={convertLead}
          open={convertOpen}
          onOpenChange={setConvertOpen}
        />
      )}

      <AlertDialog open={!!reopenLead} onOpenChange={(open) => !open && setReopenLead(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reopen this lead?</AlertDialogTitle>
            <AlertDialogDescription>
              {reopenLead?.customer_name || 'This lead'} moves back to New and its lost reason is cleared.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (reopenLead) void moveToNew(reopenLead);
                setReopenLead(null);
              }}
            >
              Reopen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );

  return { requestMove, dialogs };
}
